import {
  captureApiBaseUrlFromRequestUrl,
  forceEndSession,
  isAuthLoginResponseUrl,
  isAuthLogoutUrl,
  isAuthRefreshUrl,
  stashRefreshToken,
  tryRefreshSession,
  withUpdatedAuthorization,
} from 'app/src/shared/auth/session-lifecycle';
import { TokenStorage } from '@seedkit/vue-administration-module/public';

function resolveRequestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') {
    return input;
  }
  if (input instanceof URL) {
    return input.toString();
  }
  return input.url;
}

async function stashRefreshFromLoginResponse(
  url: string,
  response: Response,
): Promise<void> {
  if (!response.ok || !isAuthLoginResponseUrl(url)) {
    return;
  }
  try {
    const data = await response.clone().json() as { refreshToken?: string | null };
    stashRefreshToken(data.refreshToken);
  } catch {
    // ignore non-JSON login responses
  }
}

async function handleUnauthorized(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  response: Response,
  url: string,
): Promise<Response> {
  if (isAuthRefreshUrl(url) || isAuthLogoutUrl(url)) {
    forceEndSession();
    throw response;
  }

  const refreshed = await tryRefreshSession();
  if (!refreshed) {
    forceEndSession();
    throw response;
  }

  const accessToken = TokenStorage.getAccessToken();
  if (!accessToken) {
    forceEndSession();
    throw response;
  }

  const retryResponse = await fetch(input, withUpdatedAuthorization(init, accessToken));
  if (retryResponse.status === 401) {
    forceEndSession();
    throw retryResponse;
  }
  return retryResponse;
}

export const requestInterceptor = (request: RequestInfo | URL) => request;

export const customFetch = async (
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> => {
  requestInterceptor(input);
  const url = resolveRequestUrl(input);
  captureApiBaseUrlFromRequestUrl(url);

  const response = await fetch(input, init);

  await stashRefreshFromLoginResponse(url, response);

  if (response.status === 401) {
    return handleUnauthorized(input, init, response, url);
  }

  return response;
};
